'use client';

import {
  JSX,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/atoms/Button/Button';
import { Typography } from '@/components/atoms/Typography/Typography';
import { allowPageScroll, blockPageScroll } from '@/utils/storeSetters';
import { scrollToElement } from '@/utils/generic';
import { TwoColContainer } from '@/components/atoms/TwoColContainer/TwoColContainer';
import { InfoResults } from './InfoResults/InfoResults';
import { SelectedFilters } from './SelectedFilters/SelectedFilters';
import { TopNavAndSearch } from './TopNavAndSearch/TopNavAndSearch';
import { FilterSections } from './FiltersSections/FiltersSections';
import * as Styled from './ProjectFilters.styled';
import {
  IFilter,
  ITabsAndFilterGroups,
} from '../FilteredProjectsGallery/FilteredProjectsGallery.types';
import { NavMobile } from './NavMobile/NavMobile';
import ReactDOM from 'react-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useFiltersStore } from '@/store/useFiltersStore';
import { TWorkPageType } from '@/types/global';
import { usePageWidth } from '@/hooks/usePageWidth';
import { usePageStore } from '@/store/usePageStore';

interface IFilterComponentProps {
  filtersData: ITabsAndFilterGroups | undefined;
  hiddenFiltersIds?: string[];
  isPrivatePresentation?: boolean;
  workPageType: TWorkPageType;
  pageFilters: IFilter[];
}

export const ProjectFilters = ({
  filtersData,
  hiddenFiltersIds = [],
  isPrivatePresentation = false,
  pageFilters,
  workPageType,
}: IFilterComponentProps) => {

  const [filtersContainerHeight, setFiltersContainerHeight] = useState('auto');
  const [filterRowHeight, setFilterRowHeight] = useState('auto');
  const refMainFiltersContainer = useRef<HTMLDivElement>(null);
  const refSelectedFiltersContainer = useRef<HTMLDivElement>(null);
  const refAllFiltersContainer = useRef<HTMLDivElement>(null);
  const refFiltersHeadingMobile = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const selectedFilters = useFiltersStore((state) => state.selectedFilters);
  const areFiltersOpen = useFiltersStore((state) => state.areFiltersOpen);
  const toggleFilterMenu = useFiltersStore((state) => state.toggleFilterMenu);
  const locationFilterNumActive = useFiltersStore((state) => state.locationFilterNumActive);
  const search = useFiltersStore((state) => state.search);
  const width = usePageWidth();


  /** calculates menu height so opening animation can run */
  const setFiltersMenuHeight = useCallback(() => {
    if (refAllFiltersContainer.current) {
      const newHeight = `${refAllFiltersContainer.current.scrollHeight}px`;
      if (newHeight !== filtersContainerHeight) {
        setFiltersContainerHeight(newHeight);
      }
    }
  }, [filtersContainerHeight]);

  const setFiltersMenuHeightFromLocation = useCallback(() => {
    const el = refAllFiltersContainer.current;
    if (!el) return;

    // Step 1: Freeze current height
    const currentHeight = el.offsetHeight;
    setFiltersContainerHeight(`${currentHeight}px`);

    // Step 2: Wait for DOM updates → then measure new height
    // requestAnimationFrame(() => {
    //   const newHeight = el.scrollHeight;
    //   setFiltersContainerHeight(`auto`);
    // });

    if (refAllFiltersContainer.current) {
      const newHeight = `${refAllFiltersContainer.current.scrollHeight}px`;
      setFiltersContainerHeight(newHeight);
    }
  }, []);

  useEffect(() => {
    setFiltersMenuHeightFromLocation();
  }, [locationFilterNumActive, setFiltersMenuHeightFromLocation])

  /** Calculate the height of the filters container on first render */
  useLayoutEffect(() => {
    setFiltersMenuHeight();
  }, [setFiltersMenuHeight, areFiltersOpen]);

  useLayoutEffect(() => {
    setFiltersMenuHeight();
  }, [width, setFiltersMenuHeight]);

  /** Calculate the height of the filters container on menu open */
  useLayoutEffect(() => {
    setFiltersMenuHeight();
  }, [setFiltersMenuHeight]);

  /** Update the height of the selected filters row */
  useLayoutEffect(() => {
    const selectedFiltersHeight = `${refSelectedFiltersContainer.current?.scrollHeight ?? 0}px`;
    if (selectedFiltersHeight !== filterRowHeight) {
      setFilterRowHeight(selectedFiltersHeight);
    }
  }, [selectedFilters, filterRowHeight ,width]);

  useLayoutEffect(() => {


  }, [width]);

  /** Centers screen when opening filters */
  useEffect(() => {
    if (areFiltersOpen) {
      scrollToElement({
        target: refMainFiltersContainer.current,
        scrollToTop: true,
        scrollMobile: false
      })
    }
  }, [areFiltersOpen]);

  useEffect(() => {
    if (selectedFilters?.filters?.length === 0) return;
    
    scrollToElement({
      target: refMainFiltersContainer.current,
      scrollToTop: true,
      scrollMobile: false
    })
  }, [selectedFilters]);

  /** gets the button color based on the selected filters */
  const getButtonColor = useCallback(
    (id: string, collection: string) => {
      return selectedFilters && selectedFilters?.filters && selectedFilters?.filters.some(
        (filter) => filter.id === id && filter.collection === collection
      )
        ? 'primary'
        : 'secondary';
    },
    [selectedFilters]
  );

  /**
   * Toggles the filters on mobile and blocks the page scroll when opened
   * @returns {void}
   */
  const toggleFiltersMobile = (): void => {
    const wereOpen = areFiltersOpen;
    if (wereOpen) {
      allowPageScroll();
    } else {
      blockPageScroll();
    }
    toggleFilterMenu();
  };

  const isUserLogged = usePageStore((state) => state.isUserLogged);

  const getFiltersSections = useMemo(() => {
    return !isUserLogged ? filtersData?.sections?.filter((section) => {
      return section.groupVisibility !== 'only_private';
    }) : filtersData?.sections;
  }, [filtersData, isUserLogged]);

  /**
   * Handles the click on the apply filters button
   * @returns {void}
   */
  const handleApplyFiltersMobileButton = (): void => {
    toggleFiltersMobile();

    setTimeout(() => {
      scrollToElement({
        target: refFiltersHeadingMobile.current,
        scrollToTop: true,
      });
    }, 300);
  };

  const filtersTemplate = (): JSX.Element => {
    return filtersData ? (
      <Styled.FiltersContainer
        data-are-filters-open={areFiltersOpen}
        data-display-selected-filters={
          (selectedFilters?.filters?.length ?? 0) > 0 && !areFiltersOpen
        }
        shouldDisplaySelectedFilters={
          (selectedFilters?.filters?.length ?? 0) > 0 && areFiltersOpen
        }
        filtersContainerHeight={filtersContainerHeight}
        style={
          {
            '--filters-container-height': filtersContainerHeight,
            '--filter-row-height': filterRowHeight,
          } as React.CSSProperties
        }
      >
        {
          !isPrivatePresentation && (
            <TwoColContainer ref={refMainFiltersContainer} id="filters-container" className="filters-nav-container">
              <TopNavAndSearch
                filtersData={filtersData}
                isPrivatePresentation={isPrivatePresentation}
              />
            </TwoColContainer>
          ) 
        }

        <TwoColContainer
          borderTop={true}
          ref={refAllFiltersContainer}
          className="container-filters-sections"
        >
          {
            workPageType !== 'work-private' && (
              <InfoResults
                isDisplayed={(selectedFilters?.filters?.length ?? 0) > 0 || !!search?.value}
                workPageType={workPageType}
                className='hidden-mobile'
              />
            )}

          <Styled.AllFiltersContainer>
            <Styled.AllFiltersColumns data-num-sections={getFiltersSections?.length}>
              <FilterSections
                filtersData={filtersData}
                pageFilters={pageFilters}
                getButtonColor={getButtonColor}
              />
            </Styled.AllFiltersColumns>

            <SelectedFilters
              hiddenFiltersIds={hiddenFiltersIds}
              refSelectedFiltersContainer={refSelectedFiltersContainer}
            />
          </Styled.AllFiltersContainer>
        </TwoColContainer>
        <Button
          className="display-only-mobile btn-apply-filters"
          data-are-filters-empty={selectedFilters?.filters?.length === 0}
          text={{
            en: 'Apply filters',
            es: 'Aplicar filtros',
          }}
          inverseRow={true}
          callbackOnClick={handleApplyFiltersMobileButton}
        >
          <Typography
            as="span"
            text={
              selectedFilters && selectedFilters.filters?.length > 0
                ? selectedFilters.filters?.length.toString()
                : ''
            }
            size="sm"
            color="primary"
            className="filters-count"
          />
        </Button>
      </Styled.FiltersContainer>
    ) : (
      <></>
    );
  };

  return (
    filtersData && (
      <>
        {/* <Suspense>
          <QueryParamsManager
            filtersData={filtersData}
          />
        </Suspense> */}
        <Styled.FiltersHeadingMobile
          className={`display-only-mobile`}
          id="filters-heading-mobile"
          ref={refFiltersHeadingMobile}
          data-is-private-presentation={isPrivatePresentation}
        >
          <NavMobile
            filtersData={filtersData}
            isPrivatePresentation={isPrivatePresentation}
          />
          <Button
            callbackOnClick={toggleFiltersMobile}
            className="display-only-mobile"
          >
            <Typography
              as="span"
              text={'Filter '}
              size="lg"
              color="secondary"
            />
            {selectedFilters?.filters?.length === 0 ? (
              <Typography as="span" text={'+'} size="lg" color="secondary" />
            ) : (
              <Typography
                as="span"
                text={selectedFilters.filters?.length.toString()}
                size="lg"
                color="primary"
              />
            )}
          </Button>
          {/* {(selectedFilters?.filters?.length ?? 0 || search?.value) > 0 && (
            <Typography
              text={{
                en: `We have found <span>${'---'} projects </span>`,
                es: `Hemos encontrado <span>${'---'} proyectos </span>`,
              }}
              size="lg"
              className="results-message"
            />
          )} */}
          {
            workPageType !== 'work-private' && (
              <InfoResults
                isDisplayed={(selectedFilters?.filters?.length ?? 0) > 0 || !!search?.value}
                workPageType={workPageType}
                className="hidden-tablet-desktop"
              />
            )}
        </Styled.FiltersHeadingMobile>

        { // Filters mobile opens in a portal like the rest of the modals
        isMobile
          ? ReactDOM.createPortal(
              filtersTemplate(),
              document.getElementById('mobile-filters-root')!
            )
          : filtersTemplate()}
      </>
    )
  );
};
